import {
  Button, Card, Col, Form, Input, Row, Space, Switch, message,
} from 'antd';
import Layout from 'components/Layout';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPlatformOrganization } from 'api/platform';
import TabNote from 'containers/Platform/TabNote';
import {
  formItemStyle,
  formTooltip,
  goLiveMarkProps,
  optionalLabel,
  platformFormClassName,
  sectionCardProps,
  switchFieldProps,
  tabContentStyle,
  tipList,
} from 'containers/Platform/labels';
import { omniSupportPhoneRule, toOmniSupportPhone } from 'utils';
import { captureException } from 'utils/errors';
import { getHttpErrorMessage } from 'utils/http';

function PlatformClientCreate() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const useSharedStripe = Form.useWatch('use_platform_stripe_template', form);

  const onFinish = (values) => {
    const payload = { ...values };
    if (payload.use_platform_stripe_template) {
      delete payload.stripe_api_public_key;
      delete payload.stripe_api_private_key;
      delete payload.payment_webhook_secret;
    }
    payload.omni_merchant_support_phone = toOmniSupportPhone(
      payload.omni_merchant_support_phone,
    );
    if (!payload.omni_merchant_support_phone) {
      delete payload.omni_merchant_support_phone;
    }

    setLoading(true);
    createPlatformOrganization(payload)
      .then((org) => {
        message.success('Client created');
        navigate(`/platform/clients/${org.id}`);
      })
      .catch((e) => {
        captureException(e);
        message.error(getHttpErrorMessage(e));
      })
      .finally(() => setLoading(false));
  };

  return (
    <Layout
      header={(
        <Layout.Header
          title="New client"
          backTitle="Clients"
          backPath="/platform/clients"
        />
      )}
    >
      <div style={tabContentStyle}>
        <TabNote title="Before you start" style={{ marginBottom: 16 }}>
          Tip, SMS, and delivery defaults are applied based on the client type you pick here.
          Dispatcher credentials are configured afterward on the client&apos;s Dispatchers tab.
          Default delivery type stays a console setting. After creating the client, finish setup on
          its Delivery fees, Billing, and SMS tabs.
        </TabNote>
        <Card {...sectionCardProps} title="New client">
          {/* autoComplete off keeps Chrome from filling saved admin logins into the key fields. */}
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            autoComplete="off"
            className={platformFormClassName}
            initialValues={{
              is_active: true,
              currency: 'USD',
              is_catering: false,
              use_platform_stripe_template: true,
            }}
          >
            <Row gutter={[16, 0]}>
              <Col xs={24} sm={14}>
                <Form.Item name="name" label="Name" rules={[{ required: true }]} style={formItemStyle}>
                  <Input placeholder="Organization name" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={10}>
                <Form.Item name="currency" label="Currency" {...goLiveMarkProps} style={formItemStyle}>
                  <Input maxLength={3} placeholder="USD" />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item name="website" label={optionalLabel('Website')} style={formItemStyle}>
                  <Input placeholder="https://" />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  name="omni_merchant_support_phone"
                  label="POS / Omni customer-facing phone"
                  rules={[
                    { required: true, message: 'Enter the POS / Omni customer-facing phone' },
                    { whitespace: true, message: 'Enter the POS / Omni customer-facing phone' },
                    omniSupportPhoneRule,
                  ]}
                  style={formItemStyle}
                  extra="Used as the customer number by integrations that send customer messages. Restaurants may also call this number."
                >
                  <Input placeholder="1234567890" maxLength={32} />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  name="is_catering"
                  label="Catering"
                  {...switchFieldProps}
                  style={formItemStyle}
                  tooltip={formTooltip('Catering', tipList([
                    'Sets tip, SMS, and delivery defaults for catering vs standard',
                    'Shared Expedite is attached automatically',
                  ]))}
                >
                  <Switch />
                </Form.Item>
              </Col>
            </Row>

            <div
              style={{
                margin: '8px 0 16px',
                paddingTop: 12,
                borderTop: '1px solid #f0f0f0',
              }}
            >
              <div style={{ fontWeight: 500, marginBottom: 12, color: '#101112' }}>
                Stripe credentials
              </div>
              <Form.Item
                name="use_platform_stripe_template"
                label="Use Nibble shared Stripe account"
                {...switchFieldProps}
                style={formItemStyle}
                extra="Copies the shared Nibble Stripe keys. Turn off to paste keys for a Stripe account owned by this client."
              >
                <Switch />
              </Form.Item>
              {!useSharedStripe && (
                <Row gutter={[16, 0]}>
                  <Col span={24}>
                    <Form.Item
                      name="stripe_api_public_key"
                      label="Public key"
                      rules={[{ required: true }]}
                      style={formItemStyle}
                    >
                      <Input placeholder="pk_..." autoComplete="off" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="stripe_api_private_key"
                      label="Private key"
                      rules={[{ required: true }]}
                      style={formItemStyle}
                    >
                      <Input.Password placeholder="sk_..." autoComplete="new-password" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="payment_webhook_secret"
                      label="Payment webhook secret"
                      rules={[{ required: true }]}
                      style={formItemStyle}
                    >
                      <Input.Password placeholder="whsec_..." autoComplete="new-password" />
                    </Form.Item>
                  </Col>
                </Row>
              )}
            </div>

            <Space>
              <Button type="primary" htmlType="submit" loading={loading}>Create</Button>
              <Button onClick={() => navigate('/platform/clients')}>Cancel</Button>
            </Space>
          </Form>
        </Card>
      </div>
    </Layout>
  );
}

export default PlatformClientCreate;
